import { Avatar } from 'library/avatar';
import { IconButton } from 'library/buttons';
import { Divider } from 'library/divider';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from 'library/dropdown';
import { ChevronDownIcon, HamburgerMenuIcon, ThunderIcon } from 'library/icons';
import { DesktopMediaQuery, MobileOrTabletMediaQuery } from 'library/media-query';
import { Tooltip } from 'library/tooltip';
import { NotificationBar } from 'modules/shared/features/notifications';
import { SearchBar } from 'modules/shared/features/search';
import { TutorialLinkBar } from 'modules/shared/features/tutorial-link';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { authActions } from 'store/auth';
import { sidebarActions } from 'store/sidebar';
import LinearProgress from '../LinearProgress/LinearProgress';
import useAiTokenQuery from '../../hooks/useAiTokenQuery';
import { useFeatureRolloutContext } from 'modules/shared/features/feature-rollout';

type AiTokenStatus = {
    color: 'grayscale' | 'primary' | 'danger';
    message?: string;
};

function getAiTokenStatus(totalAiTokens: number, totalAiTokensUsed: number): AiTokenStatus {
    if (totalAiTokens === 0) {
        return { color: 'grayscale', message: 'Not active' };
    }

    const usagePercent = (totalAiTokensUsed / totalAiTokens) * 100;

    if (usagePercent > 90) {
        return { color: 'danger' };
    }

    return { color: 'primary' };
}

interface NavbarProps {}

const Navbar: FC<NavbarProps> = () => {
    const navigate = useNavigate();

    const { user } = useAuthSelector();

    const aiToken = useAiTokenQuery();

    const { hasFeature } = useFeatureRolloutContext();

    const tokenStatus = !aiToken.isLoading
        ? getAiTokenStatus(aiToken.data.totalAiTokens, aiToken.data.totalAiTokensUsed)
        : null;

    const tokenUsagePercent =
        !aiToken.isLoading && aiToken.data.totalAiTokens > 0
            ? (aiToken.data.totalAiTokensUsed / aiToken.data.totalAiTokens) * 100
            : 0;

    const dispatch = useDispatch();

    const handleLogout = useCallback(() => {
        dispatch(authActions.logout());

        navigate('/', { replace: true });
    }, [dispatch, navigate]);

    const handleOpenSidebar = useCallback(() => {
        dispatch(sidebarActions.open());
    }, [dispatch]);

    return (
        <nav className="relative px-6 h-[88px] bg-card-color theme-custom:bg-transparent border-b border-separator-color-light z-[18]">
            <div className="absolute inset-0 rounded-[inherit] hidden theme-custom:block theme-custom:bg-white/30 theme-custom:backdrop-blur-normal"></div>

            <div className="relative flex items-center gap-6 h-full">
                <MobileOrTabletMediaQuery>
                    <Link to="/dashboard" replace>
                        <img className="h-8" src="/logos/logo.svg" alt="" />
                    </Link>
                </MobileOrTabletMediaQuery>

                {/* Left Side */}
                <div className="flex items-center gap-4">
                    <SearchBar />
                </div>

                {/* Right Side */}
                <div className="ml-auto flex items-center gap-5">
                    <div className="flex items-center gap-10">
                        <TutorialLinkBar />

                        <Tooltip placement="bottom" content="What’s New">
                            <button
                                color="secondary"
                                aria-label="Open What’s New"
                                onClick={() => window.dispatchEvent(new Event('open-release-announcements'))}
                            >
                                <ThunderIcon />
                            </button>
                        </Tooltip>

                        <NotificationBar />
                    </div>
                    <div className="h-5 w-[1.5px] bg-separator-color rounded-lg"></div>

                    <div>
                        <Dropdown>
                            <DropdownTrigger className="flex items-center gap-3">
                                <Avatar
                                    src={user.dpUrl}
                                    alt={user.name}
                                    backgroundColor={user.preset.color}
                                    size={40}
                                />
                                <DesktopMediaQuery>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold whitespace-nowrap">{user.name}</div>
                                            <div>
                                                <ChevronDownIcon strokeWidth={2} />
                                            </div>
                                        </div>
                                        <div className="text-grayscale-500 tracking-[0.8px] text-[0.65rem]">
                                            {user.role}
                                        </div>
                                    </div>
                                </DesktopMediaQuery>
                            </DropdownTrigger>
                            <DropdownList>
                                {hasFeature('AI_TOKENS') && (
                                    <div className="px-4 py-2">
                                        <div className="font-semibold whitespace-nowrap">AI Tokens</div>
                                        {aiToken.isLoading ? (
                                            <div> </div>
                                        ) : (
                                            <>
                                                <LinearProgress
                                                    variant="determinate"
                                                    className=" mt-3"
                                                    color={tokenStatus.color}
                                                    progress={tokenUsagePercent}
                                                    message={tokenStatus.message}
                                                />
                                                <div className="mt-1">
                                                    <span className="text-xs font-medium text-gray-600">
                                                        {aiToken.data.totalAiTokens === 0 ? (
                                                            <span>
                                                                0 tokens available.{' '}
                                                                <span
                                                                    className="text-primary cursor-pointer"
                                                                    onClick={() => navigate('/support')}
                                                                >
                                                                    Contact us
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                {`${aiToken.data.totalAiTokensUsed} out of ${aiToken.data.totalAiTokens} used. `}
                                                                <span className="text-primary cursor-pointer">
                                                                    See usage
                                                                </span>
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                        <Divider className="mt-4" />
                                    </div>
                                )}

                                <DropdownItem onClick={() => navigate('/account')}>Account</DropdownItem>
                                <DropdownItem onClick={handleLogout}>
                                    <span className="text-danger-500">Sign out</span>
                                </DropdownItem>
                            </DropdownList>
                        </Dropdown>
                    </div>

                    <MobileOrTabletMediaQuery>
                        <button type="button" className="text-grayscale-500" onClick={handleOpenSidebar}>
                            <HamburgerMenuIcon />
                        </button>
                    </MobileOrTabletMediaQuery>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
